import { ZodSchema, z } from "zod";

export const VALIDATE_ON = {
	ON_SUBMIT: "onSubmit",
	ON_CHANGE: "onChange",
	ON_BLUR: "onBlur",
} as const;

export type ValidateOn = (typeof VALIDATE_ON)[keyof typeof VALIDATE_ON];

export type OnSubmitCallback<
	TSchema extends ZodSchema,
	TOnSubmitMeta extends Record<string, unknown> = Record<string, unknown>,
> = (params: {
	value: z.infer<TSchema>;
	resetForm: () => void;
	setError: (field: keyof z.infer<TSchema>, error: string) => void;
	meta?: TOnSubmitMeta;
}) => void | Promise<void>;

export type FormProps<
	TSchema extends ZodSchema,
	TOnSubmitMeta extends Record<string, unknown> = Record<string, unknown>,
> = {
	/**
	 * The zod schema of the form
	 */
	schema: TSchema;
	/**
	 * The default values of the form
	 */
	defaultValues: z.infer<TSchema>;
	/**
	 * The onSubmit function of the form
	 * @param value - The values of the form
	 * @param resetForm - The function to reset the form
	 * @param setError - The function to set the error of a field. This sets the serverError field rather than the error field. We do this so we don't conflict with the error field that is managed by the form library.
	 * @returns void or a promise
	 */
	onSubmit: OnSubmitCallback<TSchema, TOnSubmitMeta>;
	/**
	 * The validateOn prop of the form
	 * @default [VALIDATE_ON.ON_SUBMIT]
	 */
	validateOn?: ValidateOn[];

	/**
	 * The id of the form, used to identify the form in the DOM
	 * We use this to prepend all of the ids in the form to signify this unique form
	 * Therefore accessing id and data-testid will be the {formId}-{fieldname}
	 */
	id: string;

	/**
	 * The meta data to be passed to the onSubmit function within the PYForm component context. This is helpful if there are multiple onSubmit buttons, and therefore multiple onSubmit functional logics that need ot be supported.
	 */
	onSubmitMeta?: TOnSubmitMeta;

	/**
	 * Optional CSS classes to apply to the form element (use with caution)
	 */
	dangerouslySetClassName?: string;
};

export type InterfaceFieldApiState<T> = {
	state: {
		meta: {
			errors: unknown[];
			serverError?: string | undefined;
			isTouched: boolean;
			isDirty: boolean;
		};
		value: T;
	};
	name: string;
	handleChange: (value: T) => void;
	handleBlur: () => void;
	pushValue: (value: T) => void;
	removeValue: (index: number) => void;
	swapValues: (index1: number, index2: number) => void;
	moveValue: (fromIndex: number, toIndex: number) => void;
	replaceValue: (index: number, value: T) => void;
};

// Form state type
export type FormState<TSchema extends ZodSchema> = {
	values: z.infer<TSchema>;
	isDirty: boolean;
	isSubmitting: boolean;
	canSubmit: boolean;
};

// Our simplified form interface that abstracts away the library implementation
export interface PyFormApi<
	TSchema extends ZodSchema,
	TOnSubmitMeta extends Record<string, unknown> = Record<string, unknown>,
> {
	// Form state
	state: FormState<TSchema>;

	// Form actions
	reset: () => void;
	submit: (onSubmitMeta?: TOnSubmitMeta) => Promise<void>;
	validate: (addErrors?: boolean) => Promise<boolean>;

	// Field access
	Field: <T>(props: {
		name: string;
		mode: "array" | "value";
		children: (field: InterfaceFieldApiState<T>) => React.ReactNode;
		validators?: {
			onChange?: ({ value, fieldApi }: { value: T; fieldApi: InterfaceFieldApiState<T> }) => void;
			onChangeListenTo?: string[];
			onBlur?: ({ value, fieldApi }: { value: T; fieldApi: InterfaceFieldApiState<T> }) => void;
			onSubmit?: ({ value, fieldApi }: { value: T; fieldApi: InterfaceFieldApiState<T> }) => void;
		};
	}) => React.ReactNode;

	Subscribe: <T>(props: {
		selector: (state: FormState<TSchema>) => T;
		children: (state: T) => React.ReactNode;
	}) => React.ReactNode;

	getFieldValue: (name: string) => z.infer<TSchema>;
	getFormValues: () => z.infer<TSchema>;
	getFieldMeta: (name: string) => unknown;
	setFieldMeta: (...args: unknown[]) => void;
	setFieldValue: <K extends keyof z.infer<TSchema>>(name: K, value: z.infer<TSchema>[K]) => void;
}

// Form handle type for imperative API access (ref)
// This is a subset of PyFormApi focused on imperative control
export type FormHandle<TSchema extends ZodSchema> = Pick<
	PyFormApi<TSchema>,
	"reset" | "submit" | "getFieldValue" | "getFormValues" | "validate"
>;

export type FieldState<T> = {
	/**
	 * The value of the field
	 */
	value: T;
	/**
	 * The errors of the field
	 */
	errors: unknown[];
	/**
	 * The onChange event handler
	 */
	onChange: (value: T) => void;
	/**
	 * The onBlur event handler
	 */
	onBlur: () => void;
};

export type ArrayFieldState<T> = {
	/**
	 * The value of the field
	 */
	value: T[];
	/**
	 * The errors of the field
	 */
	errors: unknown[];
	/**
	 * The onChange event handler
	 */
	onChange: (value: T[]) => void;
	/**
	 * The onBlur event handler
	 */
	onBlur: () => void;
	/**
	 * The pushValue event handler, used for array fields
	 * @param data - The data to push
	 */
	pushValue: (data: T) => void;
	/**
	 * The insertValue event handler, used for array fields
	 * @param index - The index to insert the value at
	 * @param data - The data to insert
	 */
	insertValue: (index: number, data: T) => void;
	/**
	 * The removeValue event handler, used for array fields
	 * @param index - The index of the value to remove
	 */
	removeValue: (index: number) => void;
	/**
	 * The swapValues event handler, used for array fields
	 * @param index1 - The index of the first value
	 * @param index2 - The index of the second value
	 */
	swapValues: (index1: number, index2: number) => void;
	/**
	 * The replaceValue event handler, used for array fields
	 * @param index - The index of the value to replace
	 * @param value - The value to replace the old value with
	 */
	replaceValue: (index: number, value: T) => void;
	/**
	 * The shiftValue event handler, used for array fields
	 * @param oldIndex - The index of the value to shift
	 * @param newIndex - The index to shift the value to
	 * @returns The reordered array
	 */
	shiftValue: (oldIndex: number, newIndex: number) => T[];
};
